import React from 'react';
import Link, { LinkProps } from 'next/link';
import styled from 'styled-components';
import { Channel, CHANNELS, SEGMENT_PATHS } from '@sparknz/journeys-common';

import { UPFRONT_PAYMENT_ID } from 'journey/productDetails/constants/ifp.constants';
import { PRODUCT_DETAILS_PAGE } from 'shared/constants';
import { useSegment } from 'shared/hooks';
import { removeNullValuesInQuery } from 'shared/utils';
import { GalleryProduct } from './typings';

const StyledCardAnchor = styled.a`
  text-decoration: none;
`;

const channelSegmentPathMap: Partial<Record<Channel, string>> = {
  [CHANNELS.BUSINESS_SHOP]: SEGMENT_PATHS.BUSINESS,
  [CHANNELS.SPARK_DIGITAL_SHOP]: SEGMENT_PATHS.LARGE_BUSINESS_AND_GOVERNMENT,
};

function constructDestinationPath(channel: Channel, groupId: string): string {
  const basePath = channelSegmentPathMap[channel] || '';
  return `${basePath}${PRODUCT_DETAILS_PAGE}/${groupId}`;
}

function getCardLinkDestination(
  product: GalleryProduct,
  channel: Channel,
): LinkProps['href'] {
  const { groupId, offerId, defaultPlan, defaultIfpTerm } = product;

  const pageQuery = removeNullValuesInQuery({
    offerId,
    planId: defaultPlan?.id,
    ifpId: defaultIfpTerm || UPFRONT_PAYMENT_ID,
  });

  return {
    pathname: constructDestinationPath(channel, groupId),
    query: pageQuery,
  };
}

type ProductGalleryCardAnchorProps = {
  product: GalleryProduct;
  children: React.ReactChild;
  onClick?: () => void;
};

const ProductGalleryCardAnchor = ({
  product,
  onClick,
  children,
}: ProductGalleryCardAnchorProps) => {
  const { channel } = useSegment();
  const cardLinkDestination = getCardLinkDestination(product, channel);

  return (
    <Link href={cardLinkDestination} passHref legacyBehavior prefetch={false}>
      <StyledCardAnchor onClick={onClick}>{children}</StyledCardAnchor>
    </Link>
  );
};

export default ProductGalleryCardAnchor;
